/* Formatting and date arithmetic. Pure functions, no React, no services —
   safe to use from anywhere including the offline path. */

import type { Tone } from '../models';

/** Today, normalised to midnight so day counts are stable within a session. */
export function today(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Whole days from today until `iso`. Negative once overdue. */
export function daysUntil(iso: string | null | undefined): number {
  if (!iso || iso === '—') return Number.POSITIVE_INFINITY;
  const target = new Date(iso);
  if (Number.isNaN(target.getTime())) return Number.POSITIVE_INFINITY;
  target.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today().getTime()) / 86_400_000);
}

/** Whole days since `iso`. */
export function daysSince(iso: string | null | undefined): number {
  const d = daysUntil(iso);
  return d === Number.POSITIVE_INFINITY ? Number.POSITIVE_INFINITY : -d;
}

/** '14 Feb 2024'. Passes '—' and empty values straight through. */
export function fmtDate(iso: string | null | undefined): string {
  if (!iso || iso === '—') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** 'in 43 days' / '12 days ago' / 'today'. */
export function countdown(iso: string | null | undefined): string {
  const d = daysUntil(iso);
  if (d === Number.POSITIVE_INFINITY) return '—';
  if (d === 0) return 'today';
  if (d === 1) return 'tomorrow';
  if (d === -1) return 'yesterday';
  return d > 0 ? `in ${d} days` : `${Math.abs(d)} days ago`;
}

/** Overdue is bad, inside 30 days is bad, inside 90 is a warning. §17's whole point. */
export function expiryTone(iso: string | null | undefined): Tone {
  const d = daysUntil(iso);
  if (d === Number.POSITIVE_INFINITY) return 'muted';
  if (d < 0) return 'bad';
  if (d <= 30) return 'bad';
  if (d <= 90) return 'warn';
  return 'ok';
}

/** £1,234 — no pence. Costs in this app are annual or monthly totals. */
export function money(n: number): string {
  return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 });
}

/** £18.10 — used for per-seat licence costs where the pence matter. */
export function moneyPrecise(n: number): string {
  return n.toLocaleString('en-GB', { style: 'currency', currency: 'GBP', minimumFractionDigits: 2 });
}

/** 'SF' from 'Simon Ford'. */
export function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

/** Whole years between `iso` and today, for device age. */
export function yearsOld(iso: string | null | undefined): number {
  const d = daysSince(iso);
  return d === Number.POSITIVE_INFINITY ? 0 : Math.floor(d / 365);
}

/** Good morning / afternoon / evening. */
export function greeting(d = new Date()): string {
  const h = d.getHours();
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
}
